/**
 * Window-event channel for opening Sofia (app-covi) with a first message from
 * anywhere in the app — without importing the Sofia module. The Sofia module
 * already depends on agents/runs/home, so importing it back from those modules
 * would create a module-graph cycle; this decoupled channel sidesteps that. The
 * CoviProvider listens and forwards to its openWithPrompt. Mirrors
 * `debugInfoEvents.ts`.
 */

export const COVI_OPEN_WITH_PROMPT_EVENT = "coval:copilot:open-with-prompt"
export const COVI_OPEN_WITH_SUMMARY_EVENT = "coval:copilot:open-with-summary"
export const COVI_OPEN_HOME_EVENT = "coval:copilot:open-home"

export interface CoviOpenWithPromptDetail {
  prompt: string
  surface?: "panel" | "home"
}

export interface CoviOpenWithSummaryDetail {
  /** Thread title, e.g. "Run Summary". */
  title: string
  /** Summary markdown, shown as Sofia's opening message exactly as written. */
  markdown: string
}

export const COVI_HIGHLIGHT_EVENT = "coval:copilot:highlight"

export interface CoviHighlightDetail {
  id: string
  fields?: string[]
}

/**
 * Open Sofia and queue `prompt` as its first auto-sent message. Home entry
 * points can request the full-page surface; product-page callers keep the
 * contextual panel default. No-op during SSR.
 */
export function openCoviWithPrompt(
  prompt: string,
  options?: { surface?: "panel" | "home" },
): void {
  if (typeof window === "undefined") {
    return
  }
  window.dispatchEvent(
    new CustomEvent<CoviOpenWithPromptDetail>(COVI_OPEN_WITH_PROMPT_EVENT, {
      detail: { prompt, surface: options?.surface },
    }),
  )
}

/**
 * Open Sofia on a fresh thread whose first message IS the supplied summary,
 * rendered verbatim with no model round trip. The summary joins the thread's
 * history, so follow-up questions are answered against the text the user just
 * read rather than a paraphrase of it. No-op during SSR.
 */
export function openCoviWithSummary(detail: CoviOpenWithSummaryDetail): void {
  if (typeof window === "undefined") {
    return
  }
  window.dispatchEvent(
    new CustomEvent<CoviOpenWithSummaryDetail>(COVI_OPEN_WITH_SUMMARY_EVENT, {
      detail,
    }),
  )
}

/**
 * Take the user to the full-page Sofia surface, resuming the conversation in
 * progress rather than starting a blank one. Returns whether Sofia handled it
 * — false when no CoviProvider is listening, which lets a caller that had a
 * plain link to fall back on let the navigation happen instead. No-op during
 * SSR.
 */
export function openCoviHome(): boolean {
  if (typeof window === "undefined") {
    return false
  }
  // Cancelable so the listener can acknowledge it: dispatchEvent returns false
  // once something has called preventDefault.
  return !window.dispatchEvent(
    new CustomEvent(COVI_OPEN_HOME_EVENT, { cancelable: true }),
  )
}

/** Briefly highlight a resource already rendered on the current page. */
export function requestCoviHighlight(
  id: string | null | undefined,
  fields: readonly string[] = [],
): void {
  if (typeof window === "undefined" || !id) {
    return
  }
  window.dispatchEvent(
    new CustomEvent<CoviHighlightDetail>(COVI_HIGHLIGHT_EVENT, {
      detail: {
        id,
        ...(fields.length > 0 ? { fields: [...new Set(fields)] } : {}),
      },
    }),
  )
}
